import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { GitCompareArrows, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { errMsg, fmtMoney, fmtNumber } from '../lib/format';

const EstadoBadge = ({ estado }) => {
  if (estado === 'ambas') return <Badge className="bg-primary/15 text-primary border border-primary/30">Ambas</Badge>;
  if (estado === 'solo_a') return <Badge className="bg-[hsl(var(--info)/0.18)] text-[hsl(var(--info))] border border-[hsl(var(--info)/0.35)]">Solo A</Badge>;
  return <Badge className="bg-[hsl(var(--warning)/0.18)] text-[hsl(var(--warning))] border border-[hsl(var(--warning)/0.35)]">Solo B</Badge>;
};

const HeadCard = ({ label, head }) => (
  <Card className="border-border shadow-[var(--shadow-elev-1)]">
    <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Cotización {label}</CardTitle></CardHeader>
    <CardContent>
      <div className="font-mono text-[12px] text-muted-foreground">{head.quotation_number}</div>
      <div className="font-medium text-[14px] truncate">{head.nombre}</div>
      <div className="money text-lg font-semibold text-primary mt-1">{fmtMoney(head.totals?.total, head.moneda)}</div>
    </CardContent>
  </Card>
);

export default function ComparadorPage() {
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');
  const [result, setResult] = useState(null);

  const { data: cotizaciones } = useQuery({
    queryKey: ['quotations', 'all', '', ''],
    queryFn: async () => (await api.get('/quotations')).data,
  });

  const compareMutation = useMutation({
    mutationFn: async () => (await api.post('/quotations/compare', { quotation_a: idA, quotation_b: idB })).data,
    onSuccess: setResult,
    onError: (e) => toast.error(errMsg(e)),
  });

  const opciones = cotizaciones || [];

  return (
    <div className="space-y-5" data-testid="comparador-page">
      <div>
        <h2 className="text-2xl font-semibold">Comparador de cotizaciones</h2>
        <p className="text-sm text-muted-foreground">Compara conceptos y totales entre dos cotizaciones (Prefab / PV)</p>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="pt-5 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5 flex-1 min-w-[240px]">
            <span className="text-[12px] uppercase tracking-wide text-muted-foreground">Cotización A (base)</span>
            <Select value={idA} onValueChange={setIdA}>
              <SelectTrigger data-testid="compare-select-a"><SelectValue placeholder="Selecciona la cotización A" /></SelectTrigger>
              <SelectContent>
                {opciones.map((q) => <SelectItem key={q.id} value={q.id} disabled={q.id === idB}>{q.quotation_number} · {q.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ArrowRight size={18} className="text-muted-foreground mb-2.5 hidden md:block" />
          <div className="space-y-1.5 flex-1 min-w-[240px]">
            <span className="text-[12px] uppercase tracking-wide text-muted-foreground">Cotización B (comparada)</span>
            <Select value={idB} onValueChange={setIdB}>
              <SelectTrigger data-testid="compare-select-b"><SelectValue placeholder="Selecciona la cotización B" /></SelectTrigger>
              <SelectContent>
                {opciones.map((q) => <SelectItem key={q.id} value={q.id} disabled={q.id === idA}>{q.quotation_number} · {q.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => compareMutation.mutate()} disabled={!idA || !idB || compareMutation.isPending} data-testid="compare-button">
            <GitCompareArrows size={15} className="mr-1.5" /> Comparar
          </Button>
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <HeadCard label="A" head={result.a} />
            <HeadCard label="B" head={result.b} />
            <Card className="border-border shadow-[var(--shadow-elev-1)]">
              <CardHeader className="pb-1"><CardTitle className="text-[12px] uppercase tracking-wide text-muted-foreground font-medium">Diferencia total (B − A)</CardTitle></CardHeader>
              <CardContent>
                <div className={`money text-lg font-semibold ${result.delta_total > 0 ? 'text-[hsl(var(--warning))]' : result.delta_total < 0 ? 'text-primary' : ''}`} data-testid="compare-delta-total">
                  {result.delta_total > 0 ? '+' : ''}{fmtMoney(result.delta_total, result.a.moneda)}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-border shadow-[var(--shadow-elev-1)]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="compare-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="th-label">Concepto</TableHead>
                      <TableHead className="th-label">Presencia</TableHead>
                      <TableHead className="th-label text-right">Cant. A</TableHead>
                      <TableHead className="th-label text-right">Total A</TableHead>
                      <TableHead className="th-label text-right">Cant. B</TableHead>
                      <TableHead className="th-label text-right">Total B</TableHead>
                      <TableHead className="th-label text-right">Δ Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(result.conceptos || []).length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No hay conceptos para comparar</TableCell></TableRow>
                    )}
                    {(result.conceptos || []).map((r, i) => (
                      <TableRow key={i} className="hover:bg-background/40">
                        <TableCell>
                          <div className="font-medium text-[13px] max-w-[280px] truncate" title={r.descripcion}>{r.descripcion}</div>
                          <div className="text-[11px] text-muted-foreground">{r.grupo}</div>
                        </TableCell>
                        <TableCell><EstadoBadge estado={r.estado} /></TableCell>
                        <TableCell className="money">{r.a ? fmtNumber(r.a.cantidad) : '—'}</TableCell>
                        <TableCell className="money">{r.a ? fmtMoney(r.a.total, result.a.moneda) : '—'}</TableCell>
                        <TableCell className="money">{r.b ? fmtNumber(r.b.cantidad) : '—'}</TableCell>
                        <TableCell className="money">{r.b ? fmtMoney(r.b.total, result.b.moneda) : '—'}</TableCell>
                        <TableCell className={`money ${r.delta_total > 0 ? 'text-[hsl(var(--warning))]' : r.delta_total < 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                          {r.delta_total > 0 ? '+' : ''}{fmtMoney(r.delta_total, result.a.moneda)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
